import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'
import InfoTip from '../../../components/novarium/InfoTip'
import { UrgencyHourDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import {
  busiestWaitWindow, HOURLY_MIN_WAITED, peakArrivalHour, UrgencyMeasure, WAIT_WINDOW_HOURS,
} from '../urgencyAnalysis'
import { hourRangeText } from '../urgencyCauseText'
import { formatCount, formatMinutes, formatPercent } from '../urgencySummaryFormat'
import { SectionState } from '../useSectionData'
import UrgencyHourlyChart from './UrgencyHourlyChart'
import UrgencyMeasureSelector from './UrgencyMeasureSelector'

interface UrgencyHourlyTabProps {
  hourly: SectionState<UrgencyHourDTO[]>
  /** El «Medir por» compartido con la evolución. */
  measure: UrgencyMeasure
  onMeasureChange: (measure: UrgencyMeasure) => void
}

/**
 * Pestaña «Por hora»: arriba, el título y el selector de medida (el mismo de la evolución); después una frase con lo que dicen los
 * datos (la hora con más llegadas y la franja de 3 horas con más espera media) y un solo gráfico: las llegadas por hora
 * (barras) o la espera media por hora de llegada (línea). La frase solo dice lo que se calcula con los datos: la hora con
 * más llegadas y, entre las franjas de 3 horas seguidas donde cada hora tiene al menos 10 pacientes con espera medida, la de
 * mayor espera media. Si la consulta falla, avisa acá con «Reintentar»; el resto de la pantalla sigue.
 */
export default function UrgencyHourlyTab({ hourly, measure, onMeasureChange }: UrgencyHourlyTabProps) {
  const intl = useIntl()
  const hours = hourly.data ?? hourly.stale
  const isArrivals = measure === 'arrivals'

  const peak = useMemo(() => (hours ? peakArrivalHour(hours) : null), [hours])
  const waitWindow = useMemo(() => (hours ? busiestWaitWindow(hours) : null), [hours])

  const title = intl.formatMessage({ id: isArrivals ? 'UrgencySummaryHourlyTitleArrivals' : 'UrgencySummaryHourlyTitleWait' })
  const sentence = [
    peak ? intl.formatMessage({ id: 'UrgencySummaryHourlyPeak' }, {
      range: hourRangeText(intl, peak.hour, peak.hour + 1),
      count: peak.arrivals,
      countText: formatCount(intl, peak.arrivals),
      pct: formatPercent(intl, peak.sharePct),
    }) : '',
    waitWindow ? intl.formatMessage({ id: 'UrgencySummaryHourlyWait' }, {
      range: hourRangeText(intl, waitWindow.fromHour, waitWindow.toHour),
      value: formatMinutes(intl, waitWindow.avgWait),
    }) : '',
  ].filter(Boolean).join(' ')

  return (
    <section className="nv-section" aria-labelledby="nv-urg-hourly-title">
      <div className="nv-urg-card-head">
        <h2 className="nv-section-title" id="nv-urg-hourly-title">{title}</h2>
        <UrgencyMeasureSelector value={measure} onChange={onMeasureChange} />
      </div>
      {hourly.status === 'failed' ? (
        <div className="nv-urg-failed" role="status">
          <p className="nv-note">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {intl.formatMessage({ id: 'UrgencySummaryHourlyFailed' })}
          </p>
          <Button type="button" link label={intl.formatMessage({ id: 'UrgencySummaryRetry' })} onClick={hourly.retry} />
        </div>
      ) : hours === null ? (
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'UrgencySummaryHourlyLoading' })}
        </p>
      ) : (
        <div className={classNames('nv-urg-stack', { 'nv-urg-blurred': hourly.status === 'loading' })}>
          {sentence && (
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              <span>{sentence}</span>
              <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryHourlyHint' }, { hours: WAIT_WINDOW_HOURS, min: HOURLY_MIN_WAITED })} />
            </p>
          )}
          <UrgencyHourlyChart hours={hours} measure={measure} ariaLabel={title} />
        </div>
      )}
    </section>
  )
}
